import React, { useEffect, useCallback, useMemo } from 'react';
import { AutoComplete } from 'antd';
import { CloseSquareFilled, FilterOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from "react-redux";
import { changeFilter, loadingFilterData } from '../stateManagement/actions/report';
import { useLocation } from "react-router-dom";
import { PARTITION_THD, PARTITION_MAIN, ADMIN_ACCESS } from '../utils/constants';
import { useAuth } from '../hooks/useAuth';

// Extract rendering helpers outside the component to prevent re-creation
const renderTitle = (title) => <span>{title}</span>;
const renderItem = (title) => ({value: title});

const FilterBySelector = () => {
  const dispatch = useDispatch();
  const {filterData, publisherView} = useSelector((state) => state.reportReducer);
  const location = useLocation();
  const { user } = useAuth();

  const isThdPublisher = user?.subAccount && user?.access !== ADMIN_ACCESS;

  // Memoize getResources to prevent unnecessary recreations
  const getResources = useCallback(() => {
    dispatch(loadingFilterData({publisherView, partition: location.pathname.includes(PARTITION_THD) ? PARTITION_THD : PARTITION_MAIN}));
  }, [dispatch, publisherView, location]);

  // Trigger initial data loading
  useEffect(() => {
    getResources();
  }, [getResources]);

  // Memoize onChange handler to prevent unnecessary recreations
  const onChange = useCallback((value) => {
    // Ensure filterData exists and has properties before checking
    if (filterData && filterData.website && filterData.website.includes(value)) {
      dispatch(changeFilter('wbsCrossBy' + value));
      return;
    }
    
    if (filterData && filterData.publisher && filterData.publisher.includes(value)) {
      dispatch(changeFilter('publCrossBy' + value));
      return;
    }

    if (filterData && filterData.provider && filterData.provider.includes(value)) {
      dispatch(changeFilter('provCrossBy' + value));
      return;
    }

    if (filterData && filterData.label && filterData.label.includes(value)) {
      dispatch(changeFilter('labelCrossBy' + value));
      return;
    }
    
    if (value === '') {
      dispatch(changeFilter(''));
    }
  }, [dispatch, filterData]);

  // Memoize options to prevent unnecessary recalculations
  const options = useMemo(() => {
    // Provide fallback empty arrays if filterData is undefined
    const publishers = filterData?.publisher 
      ? filterData.publisher.filter((item) => item !== 'Others').map(renderItem)
      : [];
    
    const websites = filterData?.website
      ? filterData.website.filter((item) => item !== 'Others').map(renderItem)
      : [];

    const providers = filterData?.provider
      ? filterData.provider.filter((item) => item !== 'Others').map(renderItem)
      : [];

    const labels = filterData?.label
      ? filterData.label.filter((item) => item !== 'Others').map(renderItem)
      : [];  

    const finalList = [
      {
        label: 'Clear selection',
        options: [{ value: '', label: 'No filter' }]
      },
      {
        label: renderTitle('Publishers'),
        options: publishers,
      },
      {
        label: renderTitle('Websites'),
        options: websites,
      },
      {
        label: renderTitle('Labels'),
        options: labels,
      }
    ]

    if (!isThdPublisher){
        finalList.splice(1, 0, {
          label: renderTitle('Platforms'),
          options: providers,
      })
    }
    
    return finalList;
  }, [filterData, isThdPublisher]);

  return (
    <AutoComplete
      className="full-size-filter" 
      popupClassName="certain-category-search-dropdown" 
      options={options} 
      onSelect={onChange}
      defaultValue={''}
      allowClear={{ clearIcon: <CloseSquareFilled /> }}
      placeholder="Filter the selection"
      suffixIcon={<FilterOutlined/>}
      filterOption={(inputValue, option) =>
        option?.value?.toLowerCase().includes(inputValue.toLowerCase())
      }
      onClear={() => dispatch(changeFilter(''))}
    ></AutoComplete>
  );
};


export default FilterBySelector;